import InputField from '#app/components/inputs/inputField'
import { CalendarInput } from '../../inputs'
import { Form, useFetcher } from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'
import { Theme } from '#app/utils/providers/themeProvider'
import slugify from 'slugify'
import InlineURLInputField from '#app/components/inputs/inlineUrlInputField'
import UserAccountLookupInput from '#app/components/inputs/userAccountLookupInput'
import Toast from '#app/components/toastsAndMessages/toast'
import { toast } from 'sonner'
import { TypographyCaps, TypographyParagraph } from '../../typography'
import { UserType } from '#app/components/inputs/userAccountLookupInput'
import ArtistAttributionSection from '#app/components/inputs/CollectionInputs/artistAttributionSection.js'
import { UserProfile } from '@dynamic-labs/sdk-react-core'
import { bytesToMB, getPageCount } from '#app/utils/misc.js'
import ImageInputField from '#app/components/inputs/imageInput'

export default function CollectionForm({
	user,
	users,
	isImageUploading,
	setIsImageUploading,
	theme,
	formData,
	setFormData,
	selected,
	setSelected,
	requiredFields,
	errorFields,
}: {
	user: UserProfile
	users: UserType[]
	isImageUploading: boolean
	setIsImageUploading: (val: boolean) => void
	theme: Theme | undefined
	formData: any
	setFormData: (data: any) => void
	selected: UserType | undefined
	setSelected: (val: UserType) => void
	requiredFields: any[]
	errorFields: { [key: string]: { [key: string]: string } }
}) {
	const ref = useRef(null)
	const fetcher = useFetcher()
	const artistImageFetcher = useFetcher()
	const [imageError, setImageError] = useState('')
	const [imageFile, setImageFile] = useState<File | undefined>(undefined)

	const handleSetSelected = (user: UserType) => {
		setSelected(user)
		handleDataChange('artistID', user.id)
	}

	const handleUnsetSelected = () => {
		setSelected(undefined)
		const newFormData = { ...formData }
		delete newFormData.artistID
		delete newFormData.artistName
		delete newFormData.artistAvatar
		setFormData(newFormData)
	}

	const handleDataChange = (name: string, value: any) => {
		let newData = { ...formData }
		newData[name] = value
		if (name === 'name') {
			const slugifiedName = slugify(value, {
				lower: true,
				strict: true,
				trim: false,
			})
			newData.slug = slugifiedName
		} else if (name === 'slug') {
			const slugifiedSlug = slugify(value, {
				lower: true,
				strict: true,
				trim: false,
			})
			newData.slug = slugifiedSlug
		}
		setFormData(newData)
	}

	const handleArtistImageChange = async (value: string) => {
		if (imageError !== '') setImageError('')
		setIsImageUploading(true)
		toast.custom(
			t => (
				<Toast
					variant={'uploading'}
					//@ts-ignore
					content={'Uploading new image'}
					additionalClasses="w-full"
					onClose={() => toast.dismiss(t)}
				/>
			),
			{ position: 'bottom-right' },
		)

		const formData = new FormData()
		formData.append('file', value)
		try {
			await artistImageFetcher.submit(formData, {
				action: '/imageUploadAPI',
				method: 'POST',
				encType: 'multipart/form-data',
			})
		} catch (e: any) {
			setIsImageUploading(false)
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						content={'Error uploading image'}
						additionalClasses="w-full"
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			if (e && e.message) {
				setImageError(e.message)
			} else {
				setImageError('Error Storing Image')
			}
		}
	}

	const handleImageUpload = async () => {
		if (!imageFile) {
			setIsImageUploading(false)
			return
		}

		try {
			// Upload the file
			const formData = new FormData()
			formData.append('file', imageFile)

			// Show upload toast
			toast.custom(
				t => (
					<Toast
						variant={'uploading'}
						content={'Uploading featured image'}
						additionalClasses="w-full"
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)

			// Upload image to server
			const imageUploadResponse = await fetch('/imageUploadAPI', {
				method: 'POST',
				body: formData,
			})

			const imageUploadResponseJson = await imageUploadResponse.json()
			if (imageUploadResponseJson && imageUploadResponseJson.imageUrl) {
				handleDataChange('imageUrl', imageUploadResponseJson.imageUrl)
				setIsImageUploading(false)
				toast.custom(
					t => (
						<Toast
							variant={'success'}
							content={'Image Uploaded Successfully!'}
							additionalClasses="w-full"
							onClose={() => toast.dismiss(t)}
						/>
					),
					{ position: 'bottom-right' },
				)
			}
		} catch (e: any) {
			setIsImageUploading(false)
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						content={'Error uploading image'}
						additionalClasses="w-full"
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			if (e && e.message) {
				setImageError(e.message)
			} else {
				setImageError('Error Storing Image')
			}
		}
	}

	useEffect(() => {
		if (artistImageFetcher.data) {
			const imageUrl = (artistImageFetcher.data as any).imageUrl
			if (imageUrl) {
				const fd = formData.artistAvatar
				if (imageUrl && imageUrl !== fd) {
					handleDataChange('artistAvatar', imageUrl)
					setIsImageUploading(false)
					toast.custom(
						t => (
							<Toast
								variant={'success'}
								content={'Image Uploaded Successfully!'}
								additionalClasses="w-full"
								onClose={() => toast.dismiss(t)}
							/>
						),
						{ position: 'bottom-right' },
					)
				}
			}
		}
	}, [artistImageFetcher])

	// Process image file upload when a file is selected
	useEffect(() => {
		if (imageFile) {
			handleImageUpload()
		}
	}, [imageFile])

	return (
		<Form
			ref={ref}
			onSubmit={e => {
				e.preventDefault()
			}}
			className="flex w-full flex-col gap-y-4 text-textPrimaryLight dark:text-textPrimaryDark"
		>
			<InputField
				name="collection name"
				type="text"
				placeholder='Ex. "Cool Collection"'
				containerWidth="w-full"
				value={formData.name}
				// isRequired={requiredFields.includes(
				// 	requiredField => requiredField.slug == 'name')}
				displayRequired={true}
				onChange={e => {
					handleDataChange('name', e.target.value)
				}}
				isError={
					requiredFields.find(requiredField => requiredField.slug == 'name') ||
					errorFields.name !== undefined
				}
				helper={errorFields.name !== undefined ? String(errorFields.name) : ''}
			/>
			<InlineURLInputField
				name="Lore URL"
				type="slug"
				id="slug"
				displayURL="https://atomiclore.io/collections/"
				displayRequired={true}
				containerWidth="w-full"
				value={formData.slug}
				onChange={e => {
					handleDataChange('slug', e.target.value)
				}}
				helper={
					'Customize your URL on Atomic Lore. Can only contain lowercase letters, numbers, and hyphens.'
				}
				jsxHelper={
					errorFields.slug !== undefined ? (
						<TypographyParagraph
							size="xSmall"
							additionalClasses="text-red dark:text-red mt-2"
						>
							{String(errorFields.slug)}
						</TypographyParagraph>
					) : undefined
				}
				isError={
					requiredFields.find(requiredField => requiredField.slug == 'slug') ||
					errorFields.slug !== undefined
				}
			/>
			<InputField
				name="collection description"
				type="text"
				lines={3}
				placeholder='Ex. "This collection is about..."'
				displayRequired={true}
				containerWidth="w-full"
				value={formData.description}
				onChange={e => {
					handleDataChange('description', e.target.value)
				}}
				isError={requiredFields.find(
					requiredField => requiredField.slug == 'description',
				)}
			/>
			<CalendarInput
				name="creation date (Optional)"
				containerWidth="w-full"
				value={formData.date}
				onChange={e => {
					handleDataChange('date', e.target.value)
				}}
				iconPlacement="right"
				isError={requiredFields.find(
					requiredField => requiredField.slug == 'date',
				)}
			/>
			<InputField
				name="external link (Optional)"
				type="text"
				placeholder='Ex. "https://..."'
				containerWidth="w-full"
				value={formData.url}
				onChange={e => {
					handleDataChange('url', e.target.value)
				}}
				isError={requiredFields.find(
					requiredField => requiredField.slug == 'url',
				)}
			/>
			<div className="">
				<ImageInputField
					name={'Featured Image'}
					helper={
						"This will be the banner image on your Collection's profile page."
					}
					isValid={true}
					isError={
						!!requiredFields.find(
							requiredField => requiredField.slug == 'imageUrl',
						)
					}
					containerWidth="w-full"
					value={formData.imageUrl}
					uploadDescription="Recommended size: 1200x800. JPG, PNG, GIF."
					onChange={file => {
						if (file) {
							// Check file type
							const fileType = file.type
							const validTypes = ['image/jpeg', 'image/png', 'image/gif']
							if (!validTypes.includes(fileType)) {
								const errorMessage = `Invalid file type. Please upload JPG, PNG, or GIF files.`
								setImageError(errorMessage)
								toast.custom(
									t => (
										<Toast
											variant={'error'}
											content={errorMessage}
											additionalClasses="w-full"
											onClose={() => toast.dismiss(t)}
										/>
									),
									{ position: 'bottom-right' },
								)
								return
							}

							// Check file size
							const fileSize = bytesToMB(file.size)
							if (Number(fileSize) > 4.5) {
								const errorMessage = `File size is ${fileSize} MB, please upload a file smaller than 4.5 MB`
								setImageError(errorMessage)
								toast.custom(
									t => (
										<Toast
											variant={'error'}
											content={errorMessage}
											additionalClasses="w-full"
											onClose={() => toast.dismiss(t)}
										/>
									),
									{ position: 'bottom-right' },
								)
								return
							}

							setImageFile(file)
							setIsImageUploading(true)
							handleImageUpload()
						} else {
							const errorMessage = 'No file selected'
							setImageError(errorMessage)
							toast.custom(
								t => (
									<Toast
										variant={'error'}
										content={errorMessage}
										additionalClasses="w-full"
										onClose={() => toast.dismiss(t)}
									/>
								),
								{ position: 'bottom-right' },
							)
						}
					}}
					aspectRatio={1.5} // 1200x800 aspect ratio
					displayRequired={true}
				/>
			</div>
			<div className="">
				<ArtistAttributionSection
					user={user}
					users={users}
					formData={formData}
					setFormData={setFormData}
					requiredFields={requiredFields}
					errorFields={errorFields}
					selected={selected}
					handleSetSelected={handleSetSelected}
					handleUnsetSelected={handleUnsetSelected}
					handleDataChange={handleDataChange}
					handleImageChange={handleArtistImageChange}
				/>
			</div>
		</Form>
	)
}
